import React from "react";
import { Compass, MapPin, Tags, Eye, EyeOff, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { getUITheme, contrastColor } from "@/lib/uiTheme";
import { iconFor } from "@/lib/icons";

export function AreaNav({ buildings, selected, onSelect, night = false, theme, collapsed = false, onToggleCollapse, showLabels = true, onToggleLabels }) {
  const t = theme || getUITheme(night);
  return (
    <nav
      data-testid="area-nav"
      className="absolute left-3 md:left-6 top-20 md:top-24 z-30 w-[230px] md:w-[300px]"
    >
      <div
        className="rounded-xl border overflow-hidden"
        style={{ background: t.panelBg, borderColor: t.panelBorder, boxShadow: "var(--shadow-panel)" }}
      >
        <div className="px-4 pt-4 pb-2 flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md shrink-0" style={{ background: t.night ? "#FFCE24" : "#2B2E34" }}>
            <Compass size={15} color={t.night ? "#10141E" : "#FFCE24"} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display uppercase tracking-[0.16em] text-[11px] leading-none" style={{ color: t.textMuted }}>Navigate</div>
            <div className="font-display font-bold uppercase tracking-tight text-base leading-tight truncate" style={{ color: t.textStrong }}>The Areas</div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              data-testid="labels-toggle"
              onClick={onToggleLabels}
              aria-pressed={showLabels}
              title={showLabels ? "Hide labels" : "Show labels"}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors ${t.hoverClass}`}
              style={{ borderColor: t.panelBorder, color: showLabels ? t.textStrong : t.textMuted, background: showLabels ? t.itemActiveBg : "transparent" }}
            >
              {showLabels ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button
              data-testid="nav-collapse-toggle"
              onClick={onToggleCollapse}
              aria-expanded={!collapsed}
              title={collapsed ? "Expand" : "Collapse"}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors ${t.hoverClass}`}
              style={{ borderColor: t.panelBorder, color: t.textStrong }}
            >
              {collapsed ? <ChevronsUpDown size={15} /> : <ChevronsDownUp size={15} />}
            </button>
          </div>
        </div>

        {collapsed ? (
          <div className="px-4 pb-3 -mt-1">
            <button
              data-testid="labels-toggle-row"
              onClick={onToggleLabels}
              className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide transition-colors ${t.hoverClass}`}
              style={{ color: t.itemIdleText }}
            >
              <span className="flex items-center gap-2"><Tags size={14} /> Labels</span>
              <span className="text-[11px]" style={{ color: showLabels ? "#E8B900" : t.textMuted }}>{showLabels ? "On" : "Off"}</span>
            </button>
          </div>
        ) : (
        <div className="px-2 pb-2 max-h-[52vh] overflow-y-auto sm-scroll">
          <button
            data-testid="area-nav-overview"
            onClick={() => onSelect(null)}
            className={`group w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide transition-colors ${!selected ? "" : t.hoverClass}`}
            style={!selected ? { background: t.itemActiveBg, color: t.itemActiveText } : { color: t.itemIdleText }}
          >
            <span className="flex items-center gap-2"><MapPin size={14} /> City Overview</span>
            {!selected && <span className="h-2 w-2 rounded-full" style={{ background: "#FFCE24" }} />}
          </button>
          <div className="my-1 h-px" style={{ background: t.divider }} />
          {buildings.map((b) => {
            const active = selected === b.slug || selected === b.id;
            const Icon = iconFor(b.slug);
            return (
              <button
                key={b.id || b.slug}
                data-testid={`area-nav-item-${b.slug}`}
                onClick={() => onSelect(b.slug)}
                className={`group w-full flex items-center justify-between rounded-lg px-3 py-2 text-sm font-display font-semibold uppercase tracking-wide transition-colors ${active ? "" : t.hoverClass}`}
                style={active ? { background: t.itemActiveBg, color: t.itemActiveText } : { color: t.itemIdleText }}
              >
                <span className="flex items-center gap-2.5 truncate">
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md border shrink-0"
                    style={{ background: b.color, borderColor: "rgba(0,0,0,0.18)" }}
                  >
                    <Icon size={13} color={contrastColor(b.color)} />
                  </span>
                  <span className="truncate">{b.name}</span>
                </span>
                {active && <span className="h-2 w-2 rounded-full shrink-0" style={{ background: "#FFCE24" }} />}
              </button>
            );
          })}
        </div>
        )}
      </div>
    </nav>
  );
}

export default AreaNav;
